import { supabase } from '@/lib/supabase';
import { listFiles, getUserMediaFolder } from '@/lib/seaweedfs';

// Quota de stockage par défaut pour chaque compte professionnel (en octets).
export const DEFAULT_STORAGE_LIMIT_BYTES = 100 * 1024 * 1024; // 100 Mo

// Répartition de l'usage du stockage par source de fichiers.
// Tous les envois du marchand sont rangés sous `users/{userId}/...` : on
// liste ce dossier et on regroupe par sous-dossier pour TOUT compter.
export interface StorageBreakdown {
  formation: number; // formations (…/formations)
  projets: number; // fichiers de projets GestionPro (…/projets…)
  portfolio: number; // images du portfolio (…/portfolio)
  medias: number; // tout le reste : médias, branding, bannières, reçus, hébergement…
}

function categorize(subfolder: string): keyof StorageBreakdown {
  const key = (subfolder || '').toLowerCase();
  if (key.includes('formation') || key.includes('fichiercours')) return 'formation';
  if (key.includes('projet')) return 'projets';
  if (key.includes('portfolio')) return 'portfolio';
  return 'medias';
}

// Espace total utilisé par le compte : on liste récursivement le dossier
// personnel de l'utilisateur (tous ses fichiers uploadés) et on cumule les poids.
export async function getAccountStorageBreakdown(userId: number): Promise<StorageBreakdown> {
  const empty: StorageBreakdown = { formation: 0, projets: 0, portfolio: 0, medias: 0 };
  try {
    const data = await listFiles(`${getUserMediaFolder(userId)}/`);
    const breakdown: StorageBreakdown = { ...empty };
    for (const file of data.files) {
      // file.folder = `users/{userId}/{sous-dossier}` (ou `users/{userId}` à la racine)
      const parts = (file.folder || '').split('/');
      const sub = parts.length > 2 ? parts[2] : '';
      breakdown[categorize(sub)] += file.size;
    }
    return breakdown;
  } catch {
    return empty;
  }
}

// Espace total utilisé par le compte (somme de toutes les sources de fichiers).
export async function getAccountStorageUsage(userId: number): Promise<number> {
  const { formation, projets, portfolio, medias } = await getAccountStorageBreakdown(userId);
  return formation + projets + portfolio + medias;
}

// Limite de stockage du compte : 100 Mo par défaut, augmentée par les
// abonnements de stockage actifs (somme des taille_mo souscrits).
export async function getAccountStorageLimit(userId: number): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('storage_subscriptions')
      .select('taille_mo')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error || !data || data.length === 0) return DEFAULT_STORAGE_LIMIT_BYTES;

    const extraMo = data.reduce((sum, s) => sum + (Number(s.taille_mo) || 0), 0);
    return DEFAULT_STORAGE_LIMIT_BYTES + extraMo * 1024 * 1024;
  } catch {
    return DEFAULT_STORAGE_LIMIT_BYTES;
  }
}

export async function getAccountStorage(userId: number): Promise<AccountStorage> {
  const [breakdown, limitBytes] = await Promise.all([
    getAccountStorageBreakdown(userId),
    getAccountStorageLimit(userId),
  ]);

  const usedBytes = breakdown.formation + breakdown.projets + breakdown.portfolio + breakdown.medias;
  const remainingBytes = Math.max(0, limitBytes - usedBytes);
  const percentUsed = limitBytes > 0 ? Math.min(100, (usedBytes / limitBytes) * 100) : 0;

  return { usedBytes, limitBytes, remainingBytes, percentUsed, breakdown };
}

export interface AccountStorage {
  usedBytes: number;
  limitBytes: number;
  remainingBytes: number;
  percentUsed: number;
  breakdown: StorageBreakdown;
}